import arrLeft from 'common/building-blocks/assets/images/arr-left.svg';
import arrRight from 'common/building-blocks/assets/images/arr-right.svg';
import {
	COMPONENT_DISPLAY_NAME,
	COMPONENT_STATES,
	COMPONENT_TYPE,
	DEFAULT_MEDIA_QUERY_PLATFORMS,
	Pin,
} from 'common/constants';
import { CSS_PROPS, OTHER_PROPS, UNIFORM_PROPS } from 'common/constants/component-props';
import { isRTLLanguage } from 'common/i18n';
import type { SupportedLanguage } from 'common/i18n/types';
import { SliderArrowDir } from '../slider/constants';
import { createModel } from '../create-model';
import type { CreatedBuildingBlock } from '../creation-types';
import { image } from '../image/recipe';

export type SliderArrowOptions = {
	direction?: SliderArrowDir;
	language?: SupportedLanguage;
};

function getPinType(props: SliderArrowOptions) {
	if (props.language && isRTLLanguage(props.language)) {
		return props.direction === SliderArrowDir.PREV ? Pin.r : Pin.l;
	}
	return props.direction === SliderArrowDir.PREV ? Pin.l : Pin.r;
}

const iconLookup = {
	rtl: {
		[SliderArrowDir.PREV]: arrRight,
		[SliderArrowDir.NEXT]: arrLeft,
	},
	ltr: {
		[SliderArrowDir.PREV]: arrLeft,
		[SliderArrowDir.NEXT]: arrRight,
	},
};

function getArrowIcon(props: SliderArrowOptions): string {
	const isRtl = props.language ? isRTLLanguage(props.language) : false;
	const directionKey = isRtl ? 'rtl' : 'ltr';
	return iconLookup[directionKey][props.direction ?? SliderArrowDir.NEXT];
}

export const sliderArrow = (
	props: SliderArrowOptions = {}
): CreatedBuildingBlock<typeof COMPONENT_TYPE.SLIDER_ARROW> => {
	const pinType = getPinType(props);

	return createModel({
		type: COMPONENT_TYPE.SLIDER_ARROW,
		editorProps: {
			draggable: false,
			expandable: true,
		},
		name: `${COMPONENT_DISPLAY_NAME[COMPONENT_TYPE.SLIDER_ARROW]} ${props.direction}`,
		componentProps: {
			[UNIFORM_PROPS.sliderArrowDir]: props.direction,
			styles: {
				[COMPONENT_STATES.DEFAULT]: {
					[DEFAULT_MEDIA_QUERY_PLATFORMS.DEFAULT]: {
						[CSS_PROPS.position.left]: pinType === Pin.l ? '1.56vw' : '-1.56vw',
					},
				},
			},
			other: {
				[COMPONENT_STATES.DEFAULT]: {
					[DEFAULT_MEDIA_QUERY_PLATFORMS.DEFAULT]: {
						[OTHER_PROPS.pin.pinType]: pinType,
					},
				},
			},
		},
		children: [
			image({
				styles: {
					[COMPONENT_STATES.DEFAULT]: {
						[DEFAULT_MEDIA_QUERY_PLATFORMS.DEFAULT]: {
							[CSS_PROPS.background.backgroundImage]: getArrowIcon(props),
							[CSS_PROPS.size.height]: '2.90vw',
							[CSS_PROPS.size.width]: '1.61vw',
							[CSS_PROPS.layout.alignSelf]: 'center',
							[CSS_PROPS.layout.justifySelf]: 'center',
							[CSS_PROPS.position.top]: 'auto',
							[CSS_PROPS.position.right]: 'auto',
							[CSS_PROPS.position.bottom]: 'auto',
							[CSS_PROPS.position.left]: pinType === Pin.l ? '-0.19vw' : '0.19vw',
						},
						[DEFAULT_MEDIA_QUERY_PLATFORMS.MOBILE]: {
							[CSS_PROPS.size.height]: '8vw',
							[CSS_PROPS.size.width]: '8vw',
							[CSS_PROPS.layout.alignSelf]: 'center',
							[CSS_PROPS.layout.justifySelf]: 'center',
							[CSS_PROPS.position.top]: 'auto',
							[CSS_PROPS.position.right]: 'auto',
							[CSS_PROPS.position.bottom]: 'auto',
						},
					},
				},
				other: {
					[COMPONENT_STATES.DEFAULT]: {
						[DEFAULT_MEDIA_QUERY_PLATFORMS.DEFAULT]: {
							[OTHER_PROPS.pin.pinType]: pinType,
						},
					},
				},
			}),
		],
	}) satisfies CreatedBuildingBlock<typeof COMPONENT_TYPE.SLIDER_ARROW>;
};
